import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { valueToString } from "helpers/Helpers";
import useDebounce from "hooks/events/useDebounce";
import { StatusCode } from "api/protocols";
import {
  GetAccount,
  GetAccountBatchJob,
  GetAccountCount,
  PostAccountBatch,
  PostAccountBatchOrder,
  PostAccountBatchPayment,
  PostAccountBatchResolveImport,
  PostAccountBatchSchedule,
  PostImportAccounts,
  PutAccountBulkUpdate,
  PutAttachMinimumSpendBatch,
  PutSendAccountStatements,
  TAccountBatchJob,
  TAccountBatchResolveImportResult,
  TAccountSelectAllFilters,
  TGetAccount,
  TPutAccountBulkUpdate,
} from "api/rpc/2024-04/facilityAdmin/client/accounts/accounts";
import { GetMinimumSpending } from "api/rpc/2024-04/facilityAdmin/accounts/minimumSpending";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";
import Sheet from "components/sheet/Sheet";
import moment from "moment";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import Page from "components/page/Page";
import { Badge } from "components/badge/Badge";
import FileImport from "components/fileImport/FileImport";
import Search from "components/search/Search";
import DataTable from "../../../../../components/dataTable/DataTable";
import { HouseAccountType, TMinimumSpend } from "../../customer/context/contextTypes";
import "../facility/facility.scss";
import "./accounts.scss";
import { LocaleCurrency, LocaleCurrencyString } from "helpers/Locale";
import Popup from "components/popup/Popup";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import Checkbox from "components/form/checkbox/Checkbox";
import { faBarsFilter, faChevronDown, faGlobe, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew";
import Tabs from "components/tabs/Tabs";
import { TCustomerType } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import { IProduct } from "redux/reducers/models/product";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { useAddFolderProductsPicker } from "hooks/folders/useAddFolderProductsPicker";
import AccountBatchJobsTab from "./AccountBatchJobsTab";
import AccountCustomersCell from "./AccountCustomersCell";
import AccountsFilterDrawer, {
  AccountsDrawerFilters,
  countActiveAccountsDrawerFilters,
} from "./AccountsFilterDrawer";
import AccountsAppliedFilters, { AccountsAppliedFilterKey } from "./AccountsAppliedFilters";
import BatchOptionGroup from "./BatchOptionGroup";
import BatchPostModeSection, { BatchPostMode } from "./BatchPostModeSection";
import "./accountsFilterDrawer.scss";

const TABLE_LIMIT = 50;
const MAX_BATCH_SIZE = 200;
const BULK_EDIT_MIN_DAY = 1;
const BULK_EDIT_MAX_DAY = 31;

type BulkEditFormState = {
  applyLimit: boolean;
  limit: string;
  applyAccountingReference: boolean;
  accountingReferenceId: number | null;
  applyGlobal: boolean;
  globalValue: boolean;
  applySendStatement: boolean;
  sendStatementValue: boolean;
  applyAutoPay: boolean;
  autoPayValue: boolean;
  autoPayAnchorDate: string;
};

const emptyBulkEditForm = (): BulkEditFormState => ({
  applyLimit: false,
  limit: "",
  applyAccountingReference: false,
  accountingReferenceId: null,
  applyGlobal: false,
  globalValue: true,
  applySendStatement: false,
  sendStatementValue: true,
  applyAutoPay: false,
  autoPayValue: true,
  autoPayAnchorDate: "1",
});

enum AccountsPageTab {
  Accounts = 0,
  BatchJobs = 1,
}

type ChargeMode = "balance" | "fixed";

function parseNumberArray(value: string | null): number[] {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map(part => Number(part))
    .filter(part => Number.isFinite(part));
}

function asSingleId(value?: number | number[]): number | undefined {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : undefined;
  }
  return value;
}

function filtersForApi(filters: TGetAccount): TGetAccount {
  const params: TGetAccount = {
    search: filters.search || undefined,
    offset: filters.offset,
    limit: TABLE_LIMIT,
  };

  if (filters.customer_type_id?.length) {
    params.customer_type_id = filters.customer_type_id;
  }
  if (filters.cart_customer_type_id?.length) {
    params.cart_customer_type_id = filters.cart_customer_type_id;
  }
  if (filters.league_id) {
    params.league_id = filters.league_id;
  }
  if (filters.tournament_id) {
    params.tournament_id = filters.tournament_id;
  }
  if (typeof filters.has_default_payment_method === "boolean") {
    params.has_default_payment_method = filters.has_default_payment_method;
  }
  if (typeof filters.closed === "boolean") {
    params.closed = filters.closed;
  }

  return params;
}

function buildLeagueTournamentParams(query: string): { search?: string; year?: number; limit: number } {
  const trimmed = query.trim();
  const currentYear = new Date().getFullYear();

  if (!trimmed) {
    return { year: currentYear, limit: 200 };
  }

  // Allow searching other seasons by typing a year (e.g. 2025)
  if (/^\d{4}$/.test(trimmed)) {
    return { year: Number(trimmed), limit: 200 };
  }

  if (trimmed.length > 2) {
    return { search: trimmed, limit: 200 };
  }

  return { year: currentYear, limit: 200 };
}

function selectAllPayload(filters: TGetAccount): TAccountSelectAllFilters {
  const payload: TAccountSelectAllFilters = {};
  if (filters.search) {
    payload.search = filters.search;
  }
  if (filters.customer_type_id?.length) {
    payload.customer_type_id = filters.customer_type_id;
  }
  if (filters.cart_customer_type_id?.length) {
    payload.cart_customer_type_id = filters.cart_customer_type_id;
  }
  if (filters.league_id) {
    payload.league_id = filters.league_id;
  }
  if (filters.tournament_id) {
    payload.tournament_id = filters.tournament_id;
  }
  if (typeof filters.has_default_payment_method === "boolean") {
    payload.has_default_payment_method = filters.has_default_payment_method;
  }
  if (typeof filters.closed === "boolean") {
    payload.closed = filters.closed;
  }
  return payload;
}

export default function Accounts() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { authStore, facilityStore } = useAppSelector(store => store);

  const [importModalVisible, setImportModalVisible] = useState(false);
  const [sendStatementsModalVisible, setSendStatementsModalVisible] = useState(false);
  const [statementPeriod, setStatementPeriod] = useState<"current" | "last_month">("current");
  const [sendStatementsSubmitting, setSendStatementsSubmitting] = useState(false);

  const [applyMinimumModalVisible, setApplyMinimumModalVisible] = useState(false);
  const [minimumSpendOptions, setMinimumSpendOptions] = useState<TMinimumSpend[] | null>(null);
  const [selectedMinimumId, setSelectedMinimumId] = useState<number | null>(null);
  const [applyMinimumSubmitting, setApplyMinimumSubmitting] = useState(false);

  const [bulkEditModalVisible, setBulkEditModalVisible] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState<BulkEditFormState>(emptyBulkEditForm);
  const [bulkEditSubmitting, setBulkEditSubmitting] = useState(false);
  const [bulkEditAccountingReferences, setBulkEditAccountingReferences] = useState<
    Array<{ id: number; title: string; reference_number?: string | null }>
  >([]);

  const permissions = authStore?.user?.permissions;
  const facilityPermissions = facilityStore?.facility?.permissions;
  const showMultiCourseOption = facilityStore?.facility?.is_single_facility === false;
  const { Option } = Select;

  const [accounts, setAccounts] = useState<HouseAccountType[]>(undefined);
  const [matchingTotal, setMatchingTotal] = useState(0);
  const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>([]);
  const [leagues, setLeagues] = useState<ILeague[]>([]);
  const [tournaments, setTournaments] = useState<ITournament[]>([]);
  const [leagueQuery, setLeagueQuery] = useState("");
  const [tournamentQuery, setTournamentQuery] = useState("");
  const [leagueSearching, setLeagueSearching] = useState(false);
  const [tournamentSearching, setTournamentSearching] = useState(false);
  const debouncedLeagueQuery = useDebounce(leagueQuery, 400);
  const debouncedTournamentQuery = useDebounce(tournamentQuery, 400);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [chargeMode, setChargeMode] = useState<ChargeMode>("balance");
  const [chargeAmount, setChargeAmount] = useState("");
  const [chargeSubmitting, setChargeSubmitting] = useState(false);
  const [chargePostMode, setChargePostMode] = useState<BatchPostMode>("now");
  const [chargeScheduledDate, setChargeScheduledDate] = useState<Date>(() => moment().add(1, "day").toDate());
  const [chargeScheduledTime, setChargeScheduledTime] = useState("09:00");
  const [chargeSendReceipt, setChargeSendReceipt] = useState(false);

  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [sellSubmitting, setSellSubmitting] = useState(false);
  const [sellPostModalOpen, setSellPostModalOpen] = useState(false);
  const [sellPostMode, setSellPostMode] = useState<BatchPostMode>("now");
  const [sellScheduledDate, setSellScheduledDate] = useState<Date>(() => moment().add(1, "day").toDate());
  const [sellScheduledTime, setSellScheduledTime] = useState("09:00");
  const [sellSendReceipt, setSellSendReceipt] = useState(false);
  const [pendingSellLineItems, setPendingSellLineItems] = useState<Array<{ variant_id: number; quantity: number }>>(
    [],
  );
  const sellPicker = useAddFolderProductsPicker({
    open: sellModalOpen,
    maxSelections: MAX_BATCH_SIZE,
    includeVariants: true,
    onError: message => dispatch(showError(t(message))),
  });

  const [resolveImportModalOpen, setResolveImportModalOpen] = useState(false);
  const [resolveImportReview, setResolveImportReview] = useState<TAccountBatchResolveImportResult | null>(null);
  const [resolveImportReviewOpen, setResolveImportReviewOpen] = useState(false);
  const [batchJob, setBatchJob] = useState<TAccountBatchJob | null>(null);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  function getInitialFilters(searchValue: string): TGetAccount {
    const params = new URLSearchParams(searchValue);
    const offsetParam = params.get("offset");
    const parsedOffset = offsetParam ? Number(offsetParam) : 0;
    const hasPm = params.get("has_default_payment_method");
    const closed = params.get("closed");
    const leagueId = params.get("league_id");
    const tournamentId = params.get("tournament_id");

    // Default: open accounts only (`closed: false`). URL `closed=any` shows all.
    let closedFilter: boolean | undefined = false;
    if (closed === "any") {
      closedFilter = undefined;
    } else if (closed === "1" || closed === "true") {
      closedFilter = true;
    } else if (closed === "0" || closed === "false") {
      closedFilter = false;
    }

    return {
      search: params.get("search") ?? "",
      offset: Number.isFinite(parsedOffset) && parsedOffset >= 0 ? parsedOffset : 0,
      customer_type_id: parseNumberArray(params.get("customer_type_id")),
      cart_customer_type_id: parseNumberArray(params.get("cart_customer_type_id")),
      league_id: leagueId ? Number(leagueId) : undefined,
      tournament_id: tournamentId ? Number(tournamentId) : undefined,
      has_default_payment_method: hasPm === null ? undefined : hasPm === "1" || hasPm === "true",
      closed: closedFilter,
    };
  }

  const [filters, setFilters] = useState<TGetAccount>({
    ...getInitialFilters(location.search),
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [selectedTab, setSelectedTab] = useState<AccountsPageTab>(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") === "batch-jobs" ? AccountsPageTab.BatchJobs : AccountsPageTab.Accounts;
  });
  const [batchJobsRefreshKey, setBatchJobsRefreshKey] = useState(0);

  useEffect(() => {
    void loadCustomerTypes();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadLeagues(debouncedLeagueQuery, asSingleId(filters.league_id), source.token);
    return () => source.cancel();
  }, [debouncedLeagueQuery, filters.league_id]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTournaments(debouncedTournamentQuery, asSingleId(filters.tournament_id), source.token);
    return () => source.cancel();
  }, [debouncedTournamentQuery, filters.tournament_id]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccounts(filters, source.token);
    void loadMatchingTotal(filters, source.token);
    return () => source.cancel();
  }, [
    filters.search,
    filters.offset,
    filters.customer_type_id,
    filters.cart_customer_type_id,
    filters.league_id,
    filters.tournament_id,
    filters.has_default_payment_method,
    filters.closed,
  ]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.search) {
      params.set("search", filters.search);
    }
    if (filters.offset > 0) {
      params.set("offset", String(filters.offset));
    }
    if (filters.customer_type_id?.length) {
      params.set("customer_type_id", filters.customer_type_id.join(","));
    }
    if (filters.cart_customer_type_id?.length) {
      params.set("cart_customer_type_id", filters.cart_customer_type_id.join(","));
    }
    if (filters.league_id) {
      params.set("league_id", String(filters.league_id));
    }
    if (filters.tournament_id) {
      params.set("tournament_id", String(filters.tournament_id));
    }
    if (typeof filters.has_default_payment_method === "boolean") {
      params.set("has_default_payment_method", filters.has_default_payment_method ? "1" : "0");
    }
    if (filters.closed === true) {
      params.set("closed", "1");
    } else if (filters.closed === undefined) {
      // Distinguish "Any" from the default open-only (param omitted).
      params.set("closed", "any");
    }
    if (selectedTab === AccountsPageTab.BatchJobs) {
      params.set("tab", "batch-jobs");
    }

    history.replace({
      pathname: "/admin/settings/accounts",
      search: params.toString() ? `?${params.toString()}` : "",
    });
  }, [
    filters.search,
    filters.offset,
    filters.customer_type_id,
    filters.cart_customer_type_id,
    filters.league_id,
    filters.tournament_id,
    filters.has_default_payment_method,
    filters.closed,
    selectedTab,
    history,
  ]);

  useEffect(() => {
    if (!batchModalOpen || !batchJob?.id) {
      return;
    }
    if (batchJob.status === "complete" || batchJob.status === "partial" || batchJob.status === "failed") {
      return;
    }

    const timer = window.setInterval(() => {
      void pollBatchJob(batchJob.id);
    }, 2000);

    return () => window.clearInterval(timer);
  }, [batchModalOpen, batchJob?.id, batchJob?.status]);

  const selectedCount = selectAllMatching ? matchingTotal : selectedIds.length;

  const pageIds = useMemo(
    () => (accounts ?? []).map(account => account.id).filter((id): id is number => typeof id === "number"),
    [accounts],
  );

  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id) || selectAllMatching);

  const chargeEligibleOnPage = useMemo(() => {
    if (!accounts) {
      return 0;
    }
    return accounts.filter(account => {
      if (selectAllMatching || selectedIds.includes(account.id)) {
        return !!account.default_payment_method && !account.closed;
      }
      return false;
    }).length;
  }, [accounts, selectedIds, selectAllMatching]);

  async function loadCustomerTypes() {
    const typesRes = await GetCustomerType({ limit: 1000 }, false);
    if (typesRes.status === StatusCode.OK) {
      setCustomerTypes(typesRes.data ?? []);
    }
  }

  async function loadLeagues(query: string, selectedId?: number, token?: CancelToken) {
    setLeagueSearching(true);
    const res = await GetLeague(buildLeagueTournamentParams(query), false, token);

    if (token?.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      setLeagueSearching(false);
      return;
    }

    let next = res.data ?? [];
    if (selectedId && !next.some(league => league.id === selectedId)) {
      const selectedRes = await GetLeague({ id: selectedId }, false, token);
      if (!token?.reason && selectedRes.status === StatusCode.OK && selectedRes.data?.[0]) {
        next = [selectedRes.data[0], ...next];
      }
    }

    setLeagues(next);
    setLeagueSearching(false);
  }

  async function loadTournaments(query: string, selectedId?: number, token?: CancelToken) {
    setTournamentSearching(true);
    const res = await GetTournament(buildLeagueTournamentParams(query), false, token);

    if (token?.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      setTournamentSearching(false);
      return;
    }

    let next = res.data ?? [];
    if (selectedId && !next.some(tournament => tournament.id === selectedId)) {
      const selectedRes = await GetTournament({ id: selectedId }, false, token);
      if (!token?.reason && selectedRes.status === StatusCode.OK && selectedRes.data?.[0]) {
        next = [selectedRes.data[0], ...next];
      }
    }

    setTournaments(next);
    setTournamentSearching(false);
  }

  async function loadAccounts(nextFilters: TGetAccount, token?: CancelToken) {
    if (accounts !== undefined) {
      setAccounts(undefined);
    }
    const res = await GetAccount(filtersForApi(nextFilters), token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading accounts")));
      setAccounts([]);
      return;
    }

    setAccounts(res.data ?? []);
  }

  async function loadMatchingTotal(nextFilters: TGetAccount, token?: CancelToken) {
    const res = await GetAccountCount(filtersForApi({ ...nextFilters, offset: 0 }), false, token);
    if (token && token.reason) {
      return;
    }
    if (res.status === StatusCode.OK) {
      setMatchingTotal(res.data?.total ?? 0);
    }
  }

  async function importAccounts(selectedFile: File) {
    const formData = new FormData();
    formData.append("file", selectedFile);
    const importRes = await PostImportAccounts(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error importing accounts")));
      return;
    }

    dispatch(showSuccess(t("Accounts imported successfully")));
    setImportModalVisible(false);
    void loadAccounts(filters);
    void loadMatchingTotal(filters);
  }

  async function sendAccountStatements() {
    if (selectedCount < 1) {
      dispatch(showError(t("Select at least 1 account")));
      return;
    }

    setSendStatementsSubmitting(true);
    const sendStatementsRes = await PutSendAccountStatements(
      {
        ...selectionParams(),
        period: statementPeriod,
      },
      true,
    );
    setSendStatementsSubmitting(false);

    if (sendStatementsRes.status !== StatusCode.OK) {
      dispatch(showError(sendStatementsRes.message ?? t("Error sending account statements")));
      return;
    }

    dispatch(showSuccess(t("Account statements sent successfully")));
    setSendStatementsModalVisible(false);
  }

  function openSendStatementsModal() {
    setStatementPeriod("current");
    setSendStatementsModalVisible(true);
  }

  async function openApplyMinimumModal() {
    setSelectedMinimumId(null);
    setMinimumSpendOptions(null);
    setApplyMinimumModalVisible(true);

    const res = await GetMinimumSpending(null, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading minimum spending")));
      setApplyMinimumModalVisible(false);
      return;
    }

    const options = (res.data ?? []) as TMinimumSpend[];
    setMinimumSpendOptions(options);
    if (options.length === 1) {
      setSelectedMinimumId(options[0].id);
    }
  }

  async function applyMinimumSpend() {
    if (selectedCount < 1) {
      dispatch(showError(t("Select at least 1 account")));
      return;
    }
    if (!selectedMinimumId) {
      dispatch(showError(t("Select a minimum spend")));
      return;
    }

    setApplyMinimumSubmitting(true);
    const res = await PutAttachMinimumSpendBatch(
      {
        ...selectionParams(),
        minimum_id: selectedMinimumId,
      },
      true,
    );
    setApplyMinimumSubmitting(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.message ?? t("Error applying minimum spend")));
      return;
    }

    dispatch(showSuccess(t("Minimum spend applied successfully")));
    setApplyMinimumModalVisible(false);
    setSelectedMinimumId(null);
  }

  async function openBulkEditModal() {
    setBulkEditForm(emptyBulkEditForm());
    setBulkEditModalVisible(true);

    const accountingResponse = await GetAccountingReference({ group: "accounts_receivable" }, false);
    if (accountingResponse.status === StatusCode.OK) {
      setBulkEditAccountingReferences(accountingResponse.data ?? []);
    } else {
      setBulkEditAccountingReferences([]);
    }
  }

  function bulkEditHasChanges(form: BulkEditFormState): boolean {
    if (form.applyLimit && form.limit !== "" && Number.isFinite(Number(form.limit))) {
      return true;
    }
    if (form.applyAccountingReference) {
      return true;
    }
    if (form.applyGlobal) {
      return true;
    }
    if (form.applySendStatement) {
      return true;
    }
    if (form.applyAutoPay) {
      if (!form.autoPayValue) {
        return true;
      }
      const day = Number(form.autoPayAnchorDate);
      return Number.isFinite(day) && day >= BULK_EDIT_MIN_DAY && day <= BULK_EDIT_MAX_DAY;
    }
    return false;
  }

  function buildBulkEditPayload(form: BulkEditFormState): TPutAccountBulkUpdate | null {
    const payload: TPutAccountBulkUpdate = { ...selectionParams() };
    let hasField = false;

    if (form.applyLimit && form.limit !== "" && Number.isFinite(Number(form.limit))) {
      payload.limit = Number(form.limit);
      hasField = true;
    }
    if (form.applyAccountingReference) {
      payload.accounting_reference_id = form.accountingReferenceId;
      hasField = true;
    }
    if (form.applyGlobal) {
      payload.global = form.globalValue;
      hasField = true;
    }
    if (form.applySendStatement) {
      payload.send_statement = form.sendStatementValue;
      hasField = true;
    }
    if (form.applyAutoPay) {
      payload.auto_pay_balance = form.autoPayValue;
      if (form.autoPayValue) {
        const day = Number(form.autoPayAnchorDate);
        if (!Number.isFinite(day) || day < BULK_EDIT_MIN_DAY || day > BULK_EDIT_MAX_DAY) {
          return null;
        }
        payload.auto_pay_anchor_date = day;
      }
      hasField = true;
    }

    return hasField ? payload : null;
  }

  async function submitBulkEdit() {
    if (selectedCount < 1) {
      dispatch(showError(t("Select at least 1 account")));
      return;
    }

    const payload = buildBulkEditPayload(bulkEditForm);
    if (!payload) {
      dispatch(showError(t("Select at least one field to update")));
      return;
    }

    setBulkEditSubmitting(true);
    const res = await PutAccountBulkUpdate(payload, true);
    setBulkEditSubmitting(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.message ?? t("Error updating accounts")));
      return;
    }

    const updatedCount = res.data?.updated_count ?? selectedCount;
    const skippedCount = res.data?.skipped_count ?? 0;
    if (skippedCount > 0) {
      dispatch(
        showSuccess(
          t("Updated {{updated}} account(s). Skipped auto pay on {{skipped}} without a default payment method.", {
            updated: updatedCount,
            skipped: skippedCount,
          }),
        ),
      );
    } else {
      dispatch(showSuccess(t("Updated {{count}} account(s) successfully", { count: updatedCount })));
    }

    setBulkEditModalVisible(false);
    setBulkEditForm(emptyBulkEditForm());
    void loadAccounts(filters);
  }

  function navigateToNewAccount() {
    history.push({
      pathname: "/admin/settings/accounts/new",
      search: location.search,
    });
  }

  function downloadBatchTemplate() {
    const template = document.createElement("a");
    const csv = [
      "account_number,email,phone,customer_first_name,customer_last_name,name",
      "HA-1001,jane.doe@example.com,555-0100,Jane,Doe,Jane Doe",
    ].join("\n");
    template.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
    template.target = "_blank";
    template.download = "account_batch_selection_template.csv";
    template.click();
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilters({ ...filters, offset: filters.offset - TABLE_LIMIT });
        return;
      }
      case "next": {
        setFilters({ ...filters, offset: filters.offset + TABLE_LIMIT });
        return;
      }
      default:
        return;
    }
  }

  function clearSelection() {
    setSelectedIds([]);
    setSelectAllMatching(false);
  }

  function clearFilters() {
    setLeagueQuery("");
    setTournamentQuery("");
    updateFilters({
      customer_type_id: [],
      cart_customer_type_id: [],
      league_id: undefined,
      tournament_id: undefined,
      has_default_payment_method: undefined,
      closed: false,
      offset: 0,
    });
  }

  function removeAppliedFilter(key: AccountsAppliedFilterKey) {
    switch (key) {
      case "customer_type":
        updateFilters({ customer_type_id: [], offset: 0 });
        break;
      case "cart_customer_type":
        updateFilters({ cart_customer_type_id: [], offset: 0 });
        break;
      case "league":
        setLeagueQuery("");
        updateFilters({ league_id: undefined, offset: 0 });
        break;
      case "tournament":
        setTournamentQuery("");
        updateFilters({ tournament_id: undefined, offset: 0 });
        break;
      case "has_default_payment_method":
        updateFilters({ has_default_payment_method: undefined, offset: 0 });
        break;
      case "closed":
        updateFilters({ closed: false, offset: 0 });
        break;
      default:
        break;
    }
  }

  function applyDrawerFilters(drawerFilters: AccountsDrawerFilters) {
    updateFilters({
      ...drawerFilters,
      customer_type_id: drawerFilters.customer_type_id ?? [],
      cart_customer_type_id: drawerFilters.cart_customer_type_id ?? [],
      offset: 0,
    });
  }

  function updateFilters(partial: Partial<TGetAccount>) {
    clearSelection();
    setFilters(prev => ({ ...prev, ...partial, offset: partial.offset ?? 0 }));
  }

  function toggleSelectPage() {
    if (selectAllMatching) {
      setSelectAllMatching(false);
      setSelectedIds([]);
      return;
    }

    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
      return;
    }

    setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
  }

  function toggleRow(accountId: number) {
    if (selectAllMatching) {
      setSelectAllMatching(false);
      setSelectedIds(pageIds.filter(id => id !== accountId));
      return;
    }

    setSelectedIds(prev => (prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]));
  }

  function handleSelectAllMatching() {
    if (matchingTotal > MAX_BATCH_SIZE) {
      dispatch(showError(t("Selection exceeds the maximum of {{max}} accounts", { max: MAX_BATCH_SIZE })));
      return;
    }
    setSelectAllMatching(true);
    setSelectedIds([]);
  }

  function selectionParams() {
    if (selectAllMatching) {
      return { select_all_filters: selectAllPayload(filters) };
    }
    return { account_ids: selectedIds };
  }

  async function resolveImportSelection(selectedFile: File) {
    const formData = new FormData();
    formData.append("file", selectedFile);
    const res = await PostAccountBatchResolveImport(formData, true);
    setResolveImportModalOpen(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.message || t("Error resolving accounts from file")));
      return;
    }

    setResolveImportReview(res.data);
    setResolveImportReviewOpen(true);
  }

  function applyResolveImportSelection() {
    const accountIds = resolveImportReview?.account_ids ?? [];
    if (!accountIds.length) {
      dispatch(showError(t("No matched accounts to select")));
      return;
    }
    if (accountIds.length > MAX_BATCH_SIZE) {
      dispatch(showError(t("Selection exceeds the maximum of {{max}} accounts", { max: MAX_BATCH_SIZE })));
      return;
    }

    setSelectAllMatching(false);
    setSelectedIds(accountIds);
    setResolveImportReviewOpen(false);
    setResolveImportReview(null);
    setSelectedTab(AccountsPageTab.Accounts);
    dispatch(
      showSuccess(
        t("Selected {{count}} account(s) from import", {
          count: accountIds.length,
        }),
      ),
    );
  }

  function batchAccountIdsFromJob(job: TAccountBatchJob): number[] {
    const fromPayload = (job.payload?.account_ids as number[] | undefined) ?? [];
    if (fromPayload.length) {
      return fromPayload.map(Number).filter(id => Number.isFinite(id));
    }
    return (job.results ?? []).map(result => result.account_id).filter(id => Number.isFinite(id));
  }

  function batchLineItemsFromJob(job: TAccountBatchJob): Array<{ variant_id: number; quantity: number }> {
    const raw = job.payload?.line_items;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map(item => {
        const variantId = Number((item as { variant_id?: unknown })?.variant_id);
        const quantity = Math.max(1, Number((item as { quantity?: unknown })?.quantity) || 1);
        if (!Number.isFinite(variantId) || variantId <= 0) {
          return null;
        }
        return { variant_id: variantId, quantity };
      })
      .filter((item): item is { variant_id: number; quantity: number } => item != null);
  }

  async function resolveProductsFromLineItems(
    lineItems: Array<{ variant_id: number; quantity: number }>,
  ): Promise<{ products: IProduct[]; quantities: Record<number, number> } | null> {
    if (!lineItems.length) {
      return { products: [], quantities: {} };
    }

    const variantResponses = await Promise.all(
      lineItems.map(item => GetVariant({ id: item.variant_id, product: true }, false)),
    );

    const productIds = [
      ...new Set(
        variantResponses
          .map(res => (res.status === StatusCode.OK ? res.data?.[0]?.product_id : undefined))
          .filter((id): id is number => Number.isFinite(id)),
      ),
    ];

    if (!productIds.length) {
      return null;
    }

    const productsRes = await GetProduct(
      { ids: productIds, variants: true, extended_variants: true, inventory: true },
      true,
    );
    if (productsRes.status !== StatusCode.OK || !Array.isArray(productsRes.data)) {
      return null;
    }

    const productsById = new Map(productsRes.data.map(product => [product.id, product]));
    const products: IProduct[] = [];
    const quantities: Record<number, number> = {};
    const seenProductIds = new Set<number>();

    for (const lineItem of lineItems) {
      const product = [...productsById.values()].find(candidate =>
        (candidate.variants ?? []).some(variant => variant.id === lineItem.variant_id),
      );
      if (!product || seenProductIds.has(product.id)) {
        continue;
      }

      const matchedVariant =
        (product.variants ?? []).find(variant => variant.id === lineItem.variant_id) ?? product.variants?.[0];
      const orderedVariants = matchedVariant
        ? [matchedVariant, ...(product.variants ?? []).filter(variant => variant.id !== matchedVariant.id)]
        : product.variants ?? [];

      seenProductIds.add(product.id);
      products.push({ ...product, variants: orderedVariants });
      quantities[product.id] = lineItem.quantity;
    }

    return { products, quantities };
  }

  async function loadBatchSelection(job: TAccountBatchJob) {
    const accountIds = batchAccountIdsFromJob(job);
    if (!accountIds.length) {
      dispatch(showError(t("No accounts in this batch")));
      return;
    }

    setSelectAllMatching(false);
    setSelectedIds(accountIds);

    const lineItems = job.type === "order" ? batchLineItemsFromJob(job) : [];
    if (lineItems.length) {
      const resolved = await resolveProductsFromLineItems(lineItems);
      if (!resolved?.products.length) {
        setSelectedTab(AccountsPageTab.Accounts);
        dispatch(
          showError(
            t("Loaded {{count}} account(s), but could not restore products from this batch", {
              count: accountIds.length,
            }),
          ),
        );
        return;
      }

      // Keep Batches tab mounted so Accounts Search does not remount and clear selection.
      sellPicker.openPicker(resolved);
      setSellModalOpen(true);
      dispatch(
        showSuccess(
          t("Loaded {{accountCount}} account(s) and {{productCount}} product(s) from batch", {
            accountCount: accountIds.length,
            productCount: resolved.products.length,
          }),
        ),
      );
      return;
    }

    setSelectedTab(AccountsPageTab.Accounts);
    dispatch(showSuccess(t("Loaded {{count}} account(s) from batch", { count: accountIds.length })));
  }

  async function finalizeBatchAction(options: {
    type: "payment" | "order";
    postMode: BatchPostMode;
    scheduledDate: Date;
    scheduledTime: string;
    sendReceipt: boolean;
    payment?: { charge_balance: boolean; amount?: number };
    lineItems?: Array<{ variant_id: number; quantity: number }>;
  }) {
    if (selectedCount < 1) {
      dispatch(showError(t("Select at least one account")));
      return false;
    }

    if (options.postMode === "schedule") {
      if (!options.scheduledDate || !options.scheduledTime) {
        dispatch(showError(t("Enter a schedule date and time")));
        return false;
      }
      const scheduledAt = moment(
        `${moment(options.scheduledDate).format("YYYY-MM-DD")} ${options.scheduledTime}`,
        "YYYY-MM-DD HH:mm",
      );
      if (!scheduledAt.isValid() || scheduledAt.isSameOrBefore(moment())) {
        dispatch(showError(t("Schedule date must be in the future")));
        return false;
      }
    }

    if (options.postMode === "now") {
      let res;
      if (options.type === "payment") {
        const payment = options.payment;
        if (!payment) {
          dispatch(showError(t("Payment details are required")));
          return false;
        }
        res = await PostAccountBatchPayment(
          {
            ...selectionParams(),
            charge_balance: payment.charge_balance,
            amount: payment.amount,
            send_receipt: options.sendReceipt,
          },
          true,
        );
      } else {
        const lineItems = options.lineItems;
        if (!lineItems) {
          dispatch(showError(t("Line items are required")));
          return false;
        }
        res = await PostAccountBatchOrder(
          {
            ...selectionParams(),
            line_items: lineItems,
            send_receipt: options.sendReceipt,
          },
          true,
        );
      }

      if (res.status !== StatusCode.OK) {
        dispatch(showError(res.message || t("Error starting batch")));
        return false;
      }

      setBatchJob(res.data);
      setBatchModalOpen(true);
      setBatchJobsRefreshKey(prev => prev + 1);
      clearSelection();
      return true;
    }

    let draftPayload;
    if (options.type === "payment") {
      const payment = options.payment;
      if (!payment) {
        dispatch(showError(t("Payment details are required")));
        return false;
      }
      draftPayload = {
        type: "payment" as const,
        ...selectionParams(),
        charge_balance: payment.charge_balance,
        amount: payment.amount,
        send_receipt: options.sendReceipt,
      };
    } else {
      const lineItems = options.lineItems;
      if (!lineItems) {
        dispatch(showError(t("Line items are required")));
        return false;
      }
      draftPayload = {
        type: "order" as const,
        ...selectionParams(),
        line_items: lineItems,
        send_receipt: options.sendReceipt,
      };
    }

    const draftRes = await PostAccountBatch(draftPayload, true);

    if (draftRes.status !== StatusCode.OK) {
      dispatch(showError(draftRes.message || t("Error saving batch draft")));
      return false;
    }

    if (options.postMode === "draft") {
      dispatch(showSuccess(t("Batch draft saved")));
      setBatchJobsRefreshKey(prev => prev + 1);
      clearSelection();
      return true;
    }

    const scheduleRes = await PostAccountBatchSchedule(
      {
        id: draftRes.data.id,
        scheduled_at: moment(
          `${moment(options.scheduledDate).format("YYYY-MM-DD")} ${options.scheduledTime}`,
          "YYYY-MM-DD HH:mm",
        ).toISOString(),
      },
      true,
    );

    if (scheduleRes.status !== StatusCode.OK) {
      dispatch(showError(scheduleRes.message || t("Error scheduling batch")));
      return false;
    }

    dispatch(showSuccess(t("Batch scheduled")));
    setBatchJobsRefreshKey(prev => prev + 1);
    clearSelection();
    return true;
  }

  async function submitCharge() {
    if (chargeMode === "fixed") {
      const amount = Number(chargeAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        dispatch(showError(t("Enter a valid amount")));
        return;
      }
    }

    setChargeSubmitting(true);
    const ok = await finalizeBatchAction({
      type: "payment",
      postMode: chargePostMode,
      scheduledDate: chargeScheduledDate,
      scheduledTime: chargeScheduledTime,
      sendReceipt: chargeSendReceipt,
      payment: {
        charge_balance: chargeMode === "balance",
        amount: chargeMode === "fixed" ? Number(chargeAmount) : undefined,
      },
    });
    setChargeSubmitting(false);

    if (ok) {
      setChargeModalOpen(false);
      setChargePostMode("now");
      setChargeScheduledDate(moment().add(1, "day").toDate());
      setChargeScheduledTime("09:00");
      setChargeSendReceipt(false);
    }
  }

  function sellLineItemsFromProducts(
    products: IProduct[] = sellPicker.productsToAdd,
    quantities: Record<number, number> = sellPicker.productQuantities,
  ) {
    const lineItems: Array<{ variant_id: number; quantity: number }> = [];
    for (const product of products) {
      const variant = product.variants?.[0];
      if (!variant?.id) {
        continue;
      }
      lineItems.push({
        variant_id: variant.id,
        quantity: Math.max(1, Number(quantities[product.id]) || 1),
      });
    }
    return lineItems;
  }

  function prepareSellPost(products: IProduct[] = sellPicker.productsToAdd) {
    if (selectedCount < 1) {
      dispatch(showError(t("Select at least one account")));
      return;
    }

    const lineItems = sellLineItemsFromProducts(products);
    if (!lineItems.length) {
      dispatch(showError(t("Add at least one product with a sellable variant")));
      return;
    }

    setPendingSellLineItems(lineItems);
    setSellPostMode("now");
    setSellScheduledDate(moment().add(1, "day").toDate());
    setSellScheduledTime("09:00");
    setSellSendReceipt(false);
    setSellModalOpen(false);
    setSellPostModalOpen(true);
  }

  async function submitSellPost() {
    if (sellSubmitting) {
      return;
    }

    setSellSubmitting(true);
    const ok = await finalizeBatchAction({
      type: "order",
      postMode: sellPostMode,
      scheduledDate: sellScheduledDate,
      scheduledTime: sellScheduledTime,
      sendReceipt: sellSendReceipt,
      lineItems: pendingSellLineItems,
    });
    setSellSubmitting(false);

    if (ok) {
      setSellPostModalOpen(false);
      setPendingSellLineItems([]);
      sellPicker.resetPicker();
    }
  }

  async function pollBatchJob(id: number) {
    const res = await GetAccountBatchJob({ id }, false);
    if (res.status !== StatusCode.OK) {
      return;
    }
    setBatchJob(res.data);
    if (res.data.status === "complete" || res.data.status === "partial" || res.data.status === "failed") {
      void loadAccounts(filters);
      void loadMatchingTotal(filters);
      if (res.data.status === "complete") {
        dispatch(showSuccess(t("Batch job finished")));
      } else if (res.data.status === "partial") {
        dispatch(showSuccess(t("Batch finished with some failures")));
      } else {
        dispatch(showError(t("Batch finished with failures")));
      }
    }
  }

  const batchResults = batchJob?.results ?? [];
  const batchSuccessCount = batchResults.filter(result => result.status === "success").length;
  const batchFailedCount = batchResults.filter(
    result => result.status === "failed" || result.status === "skipped",
  ).length;

  function capitalizeStatus(status?: string | null): string {
    if (!status) {
      return "—";
    }
    if (status === "skipped") {
      return "Failed";
    }
    if (status === "partial") {
      return "Partial";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  const activeDrawerFilterCount = countActiveAccountsDrawerFilters(filters);

  return (
    <Page
      title={t("Accounts")}
      subtitle={t("View and manage the accounts created for your facility here.")}
      breadcrumbs={[
        { label: t("Settings"), url: "/admin/settings" },
        { label: t("Accounting"), url: "/admin/settings/accounts" },
        { label: t("Accounts"), url: "/admin/settings/accounts" },
      ]}
      primaryAction={{
        content: (
          <>
            <FontAwesomeIcon icon={faPlus} />
            <span className="ml-2">{t("New Account")}</span>
          </>
        ),
        action: navigateToNewAccount,
      }}
      multipleActionDropdownAction={{
        label: t("More options"),
        icon: <FontAwesomeIcon icon={faChevronDown} className="ml-1 multiple-action-dropdown-trigger-chevron" />,
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Import"),
              icon: "file-import",
              handler: () => setImportModalVisible(true),
              disabled: !permissions?.accounts_import || !facilityPermissions?.accounts_import,
            },
            {
              type: "handler",
              label: t("Import Selection"),
              icon: "list",
              handler: () => setResolveImportModalOpen(true),
            },
            {
              type: "handler",
              label: t("Batch Template"),
              icon: "file-csv",
              handler: downloadBatchTemplate,
            },
            {
              type: "handler",
              label: t("Minimum Spending"),
              icon: "dollar",
              handler: () =>
                history.push({
                  pathname: "/admin/settings/accounts/minimum-spending",
                  search: location.search,
                }),
            },
          ],
        },
      }}
    >
      <Tabs
        tabs={[{ content: t("Accounts") }, { content: t("Batches") }]}
        selected={selectedTab}
        onSelect={tab => setSelectedTab(tab)}
        variant="underline"
      />

      {selectedTab === AccountsPageTab.BatchJobs ? (
        <div className="mt-4 account-batch-jobs-tab">
          <AccountBatchJobsTab
            refreshKey={batchJobsRefreshKey}
            onLoadSelection={loadBatchSelection}
            onChanged={() => setBatchJobsRefreshKey(prev => prev + 1)}
          />
        </div>
      ) : (
        <div className="mt-4">
          <AccountsAppliedFilters
            filters={filters}
            customerTypes={customerTypes}
            leagues={leagues}
            tournaments={tournaments}
            onRemoveFilter={removeAppliedFilter}
            onClearAll={clearFilters}
          />
          <div className="accounts-search-row flex flex-row flex-nowrap items-center gap-3 w-full mb-4">
            <div className="accounts-search flex-1 min-w-0">
              <Search
                historyKey="account_settings"
                searchCallback={searchValue => {
                  // Remounting this Search (e.g. after switching back from Batches) re-fires the
                  // current query; skip no-op updates so load-selection IDs are not cleared.
                  if (searchValue === (filtersRef.current.search ?? "")) {
                    return;
                  }
                  updateFilters({
                    search: searchValue,
                  });
                }}
                placeholder={t("Search accounts by title, customers or account number...")}
              />
            </div>
            <Button
              type="secondary"
              size="medium"
              className="accounts-filter-button flex-shrink-0"
              onClick={() => setFilterDrawerOpen(true)}
            >
              <FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
              {activeDrawerFilterCount > 0
                ? t("Filter ({{count}})", { count: activeDrawerFilterCount })
                : t("Filter")}
            </Button>
          </div>

          {selectedCount > 0 && (
            <div className="mb-4 flex flex-row flex-wrap items-center gap-3">
              <Badge type="gray" size="medium">
                {selectAllMatching
                  ? t("All {{count}} matching selected", { count: matchingTotal })
                  : t("{{count}} selected", { count: selectedCount })}
              </Badge>
              {!selectAllMatching && matchingTotal > selectedIds.length && (
                <Button type="text" size="small" onClick={handleSelectAllMatching}>
                  {t("Select all matching ({{count}})", { count: matchingTotal })}
                </Button>
              )}
              <Button type="text" size="small" onClick={clearSelection}>
                {t("Clear")}
              </Button>
              <Button type="secondary" size="small" onClick={openBulkEditModal}>
                {t("Bulk Edit")}
              </Button>
              <Button type="secondary" size="small" onClick={() => setChargeModalOpen(true)}>
                {t("Charge Payment")}
              </Button>
              <Button
                type="secondary"
                size="small"
                onClick={() => {
                  sellPicker.openPicker();
                  setSellModalOpen(true);
                }}
              >
                {t("Sell Products")}
              </Button>
              <Button type="secondary" size="small" onClick={openSendStatementsModal}>
                {t("Send Account Statement")}
              </Button>
              <Button type="secondary" size="small" onClick={openApplyMinimumModal}>
                {t("Apply Minimum Spend")}
              </Button>
            </div>
          )}

          <DataTable
            className="table-accounts_page"
            columns={[
              {
                label: "",
                width: "40px",
                content: <Checkbox size="small" checked={allPageSelected} onChange={toggleSelectPage} />,
              },
              { label: t("Title") },
              { label: t("Customers") },
              { label: t("Account Number") },
              // { label: t("Customer Type") },
              // { label: t("Currency") },
              { label: t("Balance") },
              // { label: t("Payment Method") },
              { label: t("Limit") },
              { label: "" },
            ]}
            loading={accounts === undefined}
            footer={{
              tableLimit: TABLE_LIMIT,
              tableOffset: filters.offset,
              handleTableOffset: direction => handleTableNavigation(direction),
              disableNextOffset: !accounts || !(accounts?.length === TABLE_LIMIT),
            }}
          >
            {accounts?.map(account => {
              const accountId = account.id;
              if (accountId == null) {
                return null;
              }

              const isSelected = selectAllMatching || selectedIds.includes(accountId);
              const rowClassName = [isSelected ? "selected" : null, account.closed ? "account-row--closed" : null]
                .filter(Boolean)
                .join(" ");

              return (
                <tr key={accountId} className={rowClassName || undefined}>
                  <td
                    onClick={event => {
                      event.stopPropagation();
                      toggleRow(accountId);
                    }}
                  >
                    <Checkbox size="small" checked={isSelected} onChange={() => toggleRow(accountId)} />
                  </td>
                  <td
                    className="clickable"
                    onClick={() =>
                      history.push({
                        pathname: "/admin/settings/accounts/overview/" + String(account.id),
                        search: location.search,
                      })
                    }
                  >
                    <div className="account-row-identity">
                      <div className="account-row-identity__text">
                        <p className="table-cell-lead-text account-row-identity__title">
                          <span className="account-row-identity__name">{account.title}</span>
                          {account.closed ? (
                            <Badge type="error" size="xsmall" className="account-row-identity__closed-badge">
                              {t("Closed")}
                            </Badge>
                          ) : null}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <AccountCustomersCell account={account} />
                  </td>
                  <td>{account.account_number}</td>
                  <td>
                    <LocaleCurrency currency={account.currency} amount={account.balance} />
                  </td>

                  <td>
                    <LocaleCurrency currency={account.currency} amount={account.limit} />
                  </td>
                  <td>
                    {account.global ? (
                      <FontAwesomeIcon
                        icon={faGlobe}
                        className="account-row-identity__global"
                        title={t("Global")}
                        aria-label={t("Global")}
                      />
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </DataTable>

          <Popup
            open={bulkEditModalVisible}
            type="info"
            title={t("Bulk Edit")}
            description={t("Update shared settings on {{count}} selected house account(s). Only checked fields are changed.", {
              count: selectedCount,
            })}
            okText={t("Apply")}
            onCancel={() => setBulkEditModalVisible(false)}
            onOk={submitBulkEdit}
            loading={bulkEditSubmitting}
            okDisabled={bulkEditSubmitting || !bulkEditHasChanges(bulkEditForm)}
          >
            <div className="mt-4 flex flex-col gap-3 text-left">
              <BatchOptionGroup
                aria-label={t("Bulk edit fields")}
                options={[
                  {
                    key: "limit",
                    title: t("Credit limit"),
                    description: t("Set the same credit limit on every selected account."),
                    selected: bulkEditForm.applyLimit,
                    onSelect: () =>
                      setBulkEditForm(prev => ({ ...prev, applyLimit: !prev.applyLimit })),
                    content: (
                      <Input
                        label={t("Limit")}
                        type="number"
                        value={bulkEditForm.limit}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          setBulkEditForm(prev => ({ ...prev, limit: event.target.value }))
                        }
                      />
                    ),
                  },
                  {
                    key: "accounting_reference",
                    title: t("Accounting reference"),
                    description: t("Set or clear the accounting reference on selected accounts."),
                    selected: bulkEditForm.applyAccountingReference,
                    onSelect: () =>
                      setBulkEditForm(prev => ({
                        ...prev,
                        applyAccountingReference: !prev.applyAccountingReference,
                      })),
                    content: (
                      <Select
                        key={`bulk-ar-${bulkEditForm.accountingReferenceId ?? "none"}`}
                        label={t("Accounting Reference")}
                        defaultValue={bulkEditForm.accountingReferenceId ?? -1}
                        onChange={(value: number) =>
                          setBulkEditForm(prev => ({
                            ...prev,
                            accountingReferenceId: value === -1 ? null : value,
                          }))
                        }
                      >
                        <Option value={-1} name={t("None")}>
                          {t("None")}
                        </Option>
                        {bulkEditAccountingReferences.map(type => {
                          const label = formatAccountingReferenceLabel(type.title ?? "", type.reference_number);
                          return (
                            <Option key={type.id} value={type.id} name={label}>
                              <span>{label}</span>
                            </Option>
                          );
                        })}
                      </Select>
                    ),
                  },
                  ...(showMultiCourseOption
                    ? [
                      {
                        key: "global",
                        title: t("Multi-course account"),
                        description: t("Apply the multi-course setting to selected accounts."),
                        selected: bulkEditForm.applyGlobal,
                        onSelect: () =>
                          setBulkEditForm(prev => ({ ...prev, applyGlobal: !prev.applyGlobal })),
                        content: (
                          <BatchOptionGroup
                            role="radiogroup"
                            aria-label={t("Multi-course value")}
                            options={[
                              {
                                key: "global_on",
                                title: t("Enable"),
                                description: t("Account can be used across all courses."),
                                selected: bulkEditForm.globalValue,
                                onSelect: () => setBulkEditForm(prev => ({ ...prev, globalValue: true })),
                              },
                              {
                                key: "global_off",
                                title: t("Disable"),
                                description: t("Account is limited to this facility."),
                                selected: !bulkEditForm.globalValue,
                                onSelect: () => setBulkEditForm(prev => ({ ...prev, globalValue: false })),
                              },
                            ]}
                          />
                        ),
                      },
                    ]
                    : []),
                  {
                    key: "send_statement",
                    title: t("Send monthly statements"),
                    description: t("Apply the monthly statement email setting to selected accounts."),
                    selected: bulkEditForm.applySendStatement,
                    onSelect: () =>
                      setBulkEditForm(prev => ({
                        ...prev,
                        applySendStatement: !prev.applySendStatement,
                      })),
                    content: (
                      <BatchOptionGroup
                        role="radiogroup"
                        aria-label={t("Send statements value")}
                        options={[
                          {
                            key: "send_on",
                            title: t("Enable"),
                            selected: bulkEditForm.sendStatementValue,
                            onSelect: () =>
                              setBulkEditForm(prev => ({ ...prev, sendStatementValue: true })),
                          },
                          {
                            key: "send_off",
                            title: t("Disable"),
                            selected: !bulkEditForm.sendStatementValue,
                            onSelect: () =>
                              setBulkEditForm(prev => ({ ...prev, sendStatementValue: false })),
                          },
                        ]}
                      />
                    ),
                  },
                  {
                    key: "auto_pay",
                    title: t("Auto pay balance"),
                    description: t(
                      "Apply auto pay settings. Accounts without a default payment method are skipped when enabling.",
                    ),
                    selected: bulkEditForm.applyAutoPay,
                    onSelect: () =>
                      setBulkEditForm(prev => ({ ...prev, applyAutoPay: !prev.applyAutoPay })),
                    content: (
                      <div className="flex flex-col gap-3">
                        <BatchOptionGroup
                          role="radiogroup"
                          aria-label={t("Auto pay value")}
                          options={[
                            {
                              key: "auto_pay_on",
                              title: t("Enable"),
                              selected: bulkEditForm.autoPayValue,
                              onSelect: () => setBulkEditForm(prev => ({ ...prev, autoPayValue: true })),
                            },
                            {
                              key: "auto_pay_off",
                              title: t("Disable"),
                              selected: !bulkEditForm.autoPayValue,
                              onSelect: () => setBulkEditForm(prev => ({ ...prev, autoPayValue: false })),
                            },
                          ]}
                        />
                        {bulkEditForm.autoPayValue ? (
                          <Input
                            label={t("Auto Pay Date")}
                            type="number"
                            value={bulkEditForm.autoPayAnchorDate}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              setBulkEditForm(prev => ({
                                ...prev,
                                autoPayAnchorDate: event.target.value,
                              }))
                            }
                            prefix={t("Day of Month")}
                          />
                        ) : null}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </Popup>

          <Popup
            open={sendStatementsModalVisible}
            type="info"
            title={t("Send Account Statement")}
            description={t("Email account statements to {{count}} selected house account(s).", {
              count: selectedCount,
            })}
            okText={t("Send")}
            onCancel={() => setSendStatementsModalVisible(false)}
            onOk={sendAccountStatements}
            loading={sendStatementsSubmitting}
            okDisabled={sendStatementsSubmitting}
          >
            <div className="mt-4 flex flex-col gap-3 text-left">
              <BatchOptionGroup
                role="radiogroup"
                aria-label={t("Statement period")}
                options={[
                  {
                    key: "current",
                    title: t("Send current statement"),
                    description: t("Email each account's statement for the current month."),
                    selected: statementPeriod === "current",
                    onSelect: () => setStatementPeriod("current"),
                  },
                  {
                    key: "last_month",
                    title: t("Send last month's statement"),
                    description: t("Email each account's statement for the previous month."),
                    selected: statementPeriod === "last_month",
                    onSelect: () => setStatementPeriod("last_month"),
                  },
                ]}
              />
            </div>
          </Popup>

          <Popup
            open={applyMinimumModalVisible}
            type="info"
            title={t("Apply Minimum Spend")}
            description={t("Apply a minimum spend requirement to {{count}} selected house account(s).", {
              count: selectedCount,
            })}
            okText={t("Apply")}
            onCancel={() => setApplyMinimumModalVisible(false)}
            onOk={applyMinimumSpend}
            loading={applyMinimumSubmitting}
            okDisabled={
              applyMinimumSubmitting ||
              !selectedMinimumId ||
              minimumSpendOptions === null ||
              minimumSpendOptions.length === 0
            }
          >
            <div className="mt-4 flex flex-col gap-3 text-left">
              {minimumSpendOptions === null ? (
                <p className="table-cell-support-text">{t("Loading minimum spends…")}</p>
              ) : minimumSpendOptions.length === 0 ? (
                <p className="table-cell-support-text">
                  {t("No minimum spends configured. Create one under Minimum Spending first.")}
                </p>
              ) : (
                <BatchOptionGroup
                  role="radiogroup"
                  aria-label={t("Minimum spend")}
                  options={minimumSpendOptions.map(minimum => ({
                    key: String(minimum.id),
                    title: minimum.title,
                    description: `${LocaleCurrencyString({
                      currency: facilityStore?.facility?.default_currency_code ?? "CAD",
                      amount: minimum.amount,
                    })} · ${valueToString(minimum.structure)}`,
                    selected: selectedMinimumId === minimum.id,
                    onSelect: () => setSelectedMinimumId(minimum.id),
                  }))}
                />
              )}
              <p className="table-cell-support-text">
                {t("Accounts that already have a minimum spend will be updated to the selected minimum.")}
              </p>
            </div>
          </Popup>

          <Popup
            open={chargeModalOpen}
            type="info"
            title={t("Charge Payment")}
            description={t("Charge {{count}} selected house account(s) using each account's saved payment method.", {
              count: selectedCount,
            })}
            okText={
              chargePostMode === "draft"
                ? t("Save Draft")
                : chargePostMode === "schedule"
                  ? t("Schedule")
                  : t("Charge")
            }
            onCancel={() => setChargeModalOpen(false)}
            onOk={submitCharge}
            okDisabled={
              chargeSubmitting ||
              (chargeMode === "fixed" && !(Number(chargeAmount) > 0)) ||
              (chargePostMode === "schedule" && !chargeScheduledTime)
            }
            loading={chargeSubmitting}
          >
            <div className="mt-4 flex flex-col gap-3 text-left">
              <BatchOptionGroup
                role="radiogroup"
                aria-label={t("Charge amount")}
                options={[
                  {
                    key: "balance",
                    title: t("Charge current balance"),
                    description: t("Charge each account's outstanding balance."),
                    selected: chargeMode === "balance",
                    onSelect: () => setChargeMode("balance"),
                  },
                  {
                    key: "fixed",
                    title: t("Charge fixed amount"),
                    description: t("Charge the same amount on every selected account."),
                    selected: chargeMode === "fixed",
                    onSelect: () => setChargeMode("fixed"),
                    content: (
                      <Input
                        label={t("Amount")}
                        type="number"
                        value={chargeAmount}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setChargeAmount(event.target.value)}
                      />
                    ),
                  },
                ]}
              />
              <BatchPostModeSection
                value={chargePostMode}
                onChange={setChargePostMode}
                scheduledDate={chargeScheduledDate}
                onScheduledDateChange={setChargeScheduledDate}
                scheduledTime={chargeScheduledTime}
                onScheduledTimeChange={time => setChargeScheduledTime(time || "")}
              />
              <BatchOptionGroup
                aria-label={t("Receipt")}
                options={[
                  {
                    key: "receipt",
                    title: t("Send payment receipt"),
                    description: t("Email a payment receipt to each account customer when the charge succeeds."),
                    selected: chargeSendReceipt,
                    onSelect: () => setChargeSendReceipt(prev => !prev),
                  },
                ]}
              />
              <p className="table-cell-support-text">
                {t("{{count}} account(s) on this page have a default payment method.", {
                  count: chargeEligibleOnPage,
                })}
              </p>
            </div>
          </Popup>
        </div>
      )}

      <AddFolderProductsModal
        open={sellModalOpen}
        title={t("Sell Products")}
        supportText={t("Create an order for each of {{count}} selected house account(s) and charge the account.", {
          count: selectedCount,
        })}
        confirmText={t("Continue")}
        showQuantity
        showPrice
        showSlotUsage={false}
        okLoading={sellSubmitting}
        productQuantities={sellPicker.productQuantities}
        onQuantityChange={sellPicker.handleQuantityChange}
        onClose={() => {
          setSellModalOpen(false);
          sellPicker.resetPicker();
        }}
        onAdd={products => {
          prepareSellPost(products);
        }}
        search={sellPicker.search}
        onSearchChange={sellPicker.setSearch}
        browseFilter={sellPicker.browseFilter}
        onBrowseFilterChange={sellPicker.setBrowseFilter}
        departments={sellPicker.departments}
        departmentsLoading={sellPicker.departmentsLoading}
        uncategorizedCount={sellPicker.uncategorizedCount}
        productsToAdd={sellPicker.productsToAdd}
        availableSlotCount={MAX_BATCH_SIZE}
        totalEmptySlotCount={MAX_BATCH_SIZE}
        folderProducts={[]}
        productSearchResults={sellPicker.productSearchResults}
        productSearchTotalCount={sellPicker.productSearchTotalCount}
        productSearchPending={sellPicker.productSearchPending}
        productSearchLoadingMore={sellPicker.productSearchLoadingMore}
        productSearchSelectAllPending={sellPicker.productSearchSelectAllPending}
        onLoadMoreProducts={sellPicker.loadMoreProducts}
        onSelectAllMatchingProducts={sellPicker.selectAllMatchingProducts}
        onSelectProduct={sellPicker.handleSelectProduct}
        onUnselectProduct={sellPicker.handleUnselectProduct}
        onCreateProduct={() => {
          setSellModalOpen(false);
          sellPicker.resetPicker();
          history.push("/admin/product/new");
        }}
        onGoToProducts={() => {
          setSellModalOpen(false);
          sellPicker.resetPicker();
          history.push("/admin/product");
        }}
      />

      <Popup
        open={sellPostModalOpen}
        type="info"
        title={t("Sell Products")}
        description={t("Post batch for {{count}} selected house account(s).", {
          count: selectedCount,
        })}
        okText={
          sellPostMode === "draft" ? t("Save Draft") : sellPostMode === "schedule" ? t("Schedule") : t("Sell")
        }
        onCancel={() => {
          setSellPostModalOpen(false);
          setPendingSellLineItems([]);
        }}
        onOk={submitSellPost}
        okDisabled={sellSubmitting || (sellPostMode === "schedule" && !sellScheduledTime)}
        loading={sellSubmitting}
      >
        <div className="mt-4 flex flex-col gap-3 text-left">
          <BatchPostModeSection
            value={sellPostMode}
            onChange={setSellPostMode}
            scheduledDate={sellScheduledDate}
            onScheduledDateChange={setSellScheduledDate}
            scheduledTime={sellScheduledTime}
            onScheduledTimeChange={time => setSellScheduledTime(time || "")}
          />
          <BatchOptionGroup
            aria-label={t("Receipt")}
            options={[
              {
                key: "receipt",
                title: t("Send order receipt"),
                description: t("Email an order receipt to each account customer when the sale succeeds."),
                selected: sellSendReceipt,
                onSelect: () => setSellSendReceipt(prev => !prev),
              },
            ]}
          />
        </div>
      </Popup>

      <Popup
        open={batchModalOpen}
        type="info"
        title={t("Batch Job")}
        description={
          batchJob?.status === "complete" ||
            batchJob?.status === "partial" ||
            batchJob?.status === "failed"
            ? t("Finished with {{success}} succeeded, {{failed}} failed.", {
              success: batchSuccessCount,
              failed: batchFailedCount,
            })
            : t("Processing house accounts…")
        }
        okText={t("Close")}
        onOk={() => {
          setBatchModalOpen(false);
          setBatchJob(null);
          setBatchJobsRefreshKey(prev => prev + 1);
        }}
        onCancel={() => {
          setBatchModalOpen(false);
          setBatchJob(null);
          setBatchJobsRefreshKey(prev => prev + 1);
        }}
      >
        <div className="mt-4 text-left" style={{ maxHeight: 240, overflowY: "auto" }}>
          <p className="mb-2">
            {t("Status")}: {capitalizeStatus(batchJob?.status)}
          </p>
          {batchResults.map(result => (
            <p key={`${result.account_id}-${result.status}-${result.message}`} className="table-cell-support-text">
              #{result.account_id}: {capitalizeStatus(result.status)} — {result.message}
            </p>
          ))}
        </div>
      </Popup>

      <AccountsFilterDrawer
        open={filterDrawerOpen}
        appliedFilters={filters}
        customerTypes={customerTypes}
        leagues={leagues}
        tournaments={tournaments}
        leagueSearching={leagueSearching}
        tournamentSearching={tournamentSearching}
        onLeagueSearch={setLeagueQuery}
        onTournamentSearch={setTournamentQuery}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={applyDrawerFilters}
      />

      <FileImport onCancel={() => setImportModalVisible(false)} onOk={importAccounts} open={importModalVisible} />

      <FileImport
        onCancel={() => setResolveImportModalOpen(false)}
        onOk={resolveImportSelection}
        open={resolveImportModalOpen}
      />

      <Sheet
        title={t("Import Selection")}
        open={resolveImportReviewOpen}
        size="small"
        closable
        onCancel={() => {
          setResolveImportReviewOpen(false);
          setResolveImportReview(null);
        }}
        onOk={applyResolveImportSelection}
        okText={t("Select Matched")}
        okDisabled={!resolveImportReview?.matched_count}
      >
        <div className="flex flex-col gap-2">
          <p>
            {t("Matched")}: {resolveImportReview?.matched_count ?? 0}
          </p>
          <p>
            {t("Ambiguous")}: {resolveImportReview?.ambiguous_count ?? 0}
          </p>
          <p>
            {t("Unmatched")}: {resolveImportReview?.unmatched_count ?? 0}
          </p>
          {(resolveImportReview?.ambiguous_count ?? 0) > 0 || (resolveImportReview?.unmatched_count ?? 0) > 0 ? (
            <p className="table-cell-support-text">
              {t("Only uniquely matched accounts will be selected. Review ambiguous and unmatched rows in your file.")}
            </p>
          ) : null}
        </div>
      </Sheet>
    </Page>
  );
}
